import React, { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@shopify/polaris";
import Currency from "components/format/currency/currency";
import { useTranslation } from "react-i18next";
import PrettyDate from "components/format/PrettyDate";
import _ from "lodash";
import { Column, FooterRow } from "types/Datatable";
import { hasPdfExtension } from "utils/helperFunctions";
import useIsPhone from "features/files/use-is-phone";
import DataTablePhoneCard from "./DataTablePhoneCard";

type Props = {
  collection: any[];
  columns: Column[];
  isTotalsDisabled?: boolean;
  maxBodyHeight?: number | string;
  /**
   * Makes the whole row clickable, navigating to the URL this returns for the
   * row's item. Cells that are themselves links/buttons keep their own behavior
   * (they stop propagation), so they don't double-trigger the row navigation.
   */
  rowUrl?: (item: any) => string | undefined;
  /**
   * Like `rowUrl`, but for a row that opens something other than a page (a modal, say): the row, and the
   * phone card, call this with the row's item. `rowUrl` wins when both are given.
   */
  onRowClick?: (item: any) => void;
  /**
   * Extra classes for a row (desktop <tr> and the phone card). A non-empty
   * value replaces the default white/striped background so a highlight (e.g.
   * a red warning row) is not overridden by the striping class.
   */
  rowClassName?: (item: any) => string | undefined;
  /**
   * Extra rows pinned under the table's own data — a month's per-currency
   * totals, say — placed under the columns they belong to instead of sitting
   * as loose text above the table. Independent of `isTotalsDisabled`'s
   * auto-summed totals row: the caller supplies these values itself (often
   * from a server-side aggregate the table's own rows do not fully cover,
   * e.g. only one page of a paginated month).
   */
  footerRows?: FooterRow[];
};

type RouterLinkProps = {
  to: string;
  className?: string;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

const RouterLink = ({ to, children, className, onClick }: RouterLinkProps) => {
  const isExternal = /^https?:\/\//.test(to);

  if (isExternal) {
    return (
      <a
        href={to}
        className={className}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

// The `max-md:` sizes only matter in the phone card: from 768 px up the table is drawn.
const buttonBaseClass =
  "inline-flex items-center gap-2 px-3 py-1.5 max-md:min-h-11 rounded-sm border border-[#E6E1DB] text-sm text-[#3F3227] bg-white hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

const iconButtonClass =
  "inline-flex items-center justify-center w-8 h-8 max-md:min-h-11 max-md:min-w-11 rounded-sm border border-[#E6E1DB] bg-white hover:bg-gray-50 text-[#3F3227] disabled:opacity-40 disabled:cursor-not-allowed";

// The sums and the footer rows under a list of phone cards.
const PHONE_SUMMARY_BOX_CLASS =
  "rounded-sm border border-[#E6E1DB] bg-gray-50 p-3 mb-3 text-sm space-y-1.5";

const wrapperStyle: React.CSSProperties = {
  border: "1px solid #E6E1DB",
  borderRadius: "4px",
  backgroundColor: "#FFFFFF",
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#F9FAFB",
  color: "#475467",
  borderBottom: "1px solid #E4E3E1",
};

const totalsRowStyle: React.CSSProperties = {
  backgroundColor: "#F9FAFB",
  borderBottom: "1px solid #E4E3E1",
};

const bodyCellStyle: React.CSSProperties = {
  borderBottom: "1px solid #F2F1EF",
};

const getBodyRowStyle = (rowIndex: number): React.CSSProperties => ({
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "rgba(249, 250, 251, 0.7)",
});

const DataTable: React.FC<Props> = ({
  collection,
  columns,
  isTotalsDisabled = false,
  maxBodyHeight,
  rowUrl,
  onRowClick,
  rowClassName,
  footerRows,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  const isRowOpenable = !!(rowUrl || onRowClick);

  const openRow = (item: any) => {
    if (rowUrl) {
      const url = rowUrl(item);
      if (url) navigate(url);
      return;
    }
    onRowClick?.(item);
  };

  /**
   * `isDisabled` may be a flat boolean for the whole column, or a predicate
   * when only some rows should be disabled — the same shape the className props
   * already accept. Without the predicate form, an action that only applies to
   * some rows (deleting a DRAFT invoice, say) has no way to disable itself.
   */
  const resolveDisabled = (
    isDisabled: boolean | ((item: any) => boolean) | undefined,
    item: any
  ) => (typeof isDisabled === "function" ? isDisabled(item) : !!isDisabled);

  const resolveClassName = (
    className: string | ((item: any) => string | undefined) | undefined,
    item: any
  ) => {
    if (!className) return "";
    return (typeof className === "function" ? className(item) : className) || "";
  };

  const resolveRowClassName = (item: any) => {
    const value = rowClassName?.(item);
    return typeof value === "string" ? value.trim() : "";
  };

  const totals: any[] = columns.map((column: Column) => {
    if (column.type === "currency" || column.type === "number") {
      return 0;
    }
    return "";
  });

  const renderClickableColumn = (column: Column, item: any, value: any) => {
    const isPdf = hasPdfExtension(item.title);
    const isIconVisible = item?.isVisible ?? true;
    const isButtonDisable = item?.disabled ?? false;

    return column.type !== "specificAction" ? (
      <div
        style={{
          cursor: column.onClick ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
        }}
        onClick={() =>
          isIconVisible && !resolveDisabled(column.isDisabled, item)
            ? column.onClick?.(item)
            : undefined
        }
      >
        {column.type === "clickabledate" ? (
          value ? (
            <PrettyDate
              dateString={value || ""}
              showTime={!!column.isShowTime}
            />
          ) : (
            <span>-</span>
          )
        ) : column.type === "downloadurl" || column.type === "actionBtn" ? (
          <button
            type="button"
            className={`${iconButtonClass} ${resolveClassName(
              column.buttonClassName,
              item
            )}`}
            disabled={
              !isIconVisible ||
              resolveDisabled(column.isDisabled, item) ||
              item.isEditButtonDisabled
            }
            onClick={(e) => {
              e.stopPropagation();
              column.onClick?.(item);
            }}
          >
            {column.Icon && (
              <column.Icon
                className={`w-4 h-4 ${resolveClassName(
                  column.iconClassName,
                  item
                )}`}
              />
            )}
          </button>
        ) : column.type === "toggleValue" ? (
          isButtonDisable ? (
            <PrettyDate
              dateString={item?.[column.key1 || column.key]}
              showTime={true}
            />
          ) : (
            <button
              type="button"
              className={buttonBaseClass}
              disabled={
                !isIconVisible ||
                resolveDisabled(column.isDisabled, item) ||
                isButtonDisable
              }
              onClick={(e) => {
                e.stopPropagation();
                column.onClick?.(item);
              }}
            >
              {column.content}
            </button>
          )
        ) : (
          <>{value === null || value === undefined ? "-" : value}</>
        )}
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          className={`${iconButtonClass} ${resolveClassName(
            column.buttonClassName,
            item
          )}`}
          disabled={!(isPdf && isIconVisible)}
          onClick={(e) => {
            e.stopPropagation();
            if (isPdf && isIconVisible) {
              column.onClick?.(item);
            }
          }}
        >
          {column.Icon && (
            <column.Icon
              className={`w-4 h-4 ${resolveClassName(
                column.iconClassName,
                item
              )}`}
            />
          )}
        </button>
      </div>
    );
  };

  const renderColumnValue = (column: Column, item: any): React.ReactNode => {
    let value = _.get(item, column.key, "-");
    // Normalize null/undefined to "-"
    if (value === null || value === undefined) {
      value = "-";
    }
    value = column.translationRequired ? t(value) : value;
    const resolveUrl = () => {
      if (!column.url) return undefined;
      return typeof column.url === "function"
        ? column.url(item)
        : _.get(item, column.url, "#");
    };
    const wrapWithLink = (content: React.ReactNode) => {
      const url = resolveUrl();
      if (!url || url === "#") {
        return content;
      }
      return (
        <RouterLink
          className="text-[#3F3227] no-underline transition-colors hover:text-[#B54708] hover:underline"
          to={url}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </RouterLink>
      );
    };

    switch (column.type) {
      case "custom":
        return column.render ? column.render(item) : null;
      case "badge":
        return wrapWithLink(
          <Badge
            tone={
              item.tone === "success" ||
              item.tone === "critical" ||
              item.tone === "attention" ||
              item.tone === "warning"
                ? item.tone
                : "info"
            }
          >
            {value}
          </Badge>
        );
      case "html_node":
        return <div dangerouslySetInnerHTML={{ __html: value }} />;
      case "currency": {
        const currency = (
          <Currency value={value} isSymbol={true} isDecimal={true} />
        );
        const valueClass = resolveClassName(column.valueClassName, item);
        const valueNode = valueClass ? (
          <span className={valueClass}>{currency}</span>
        ) : (
          currency
        );
        // Kept inside the "currency" type (rather than a "custom" column) so
        // the totals row still sums it — see the Column.indicator comment.
        const indicatorNode = column.indicator?.(item);
        return wrapWithLink(
          indicatorNode ? (
            <span className="inline-flex items-center justify-end gap-1">
              {valueNode}
              {indicatorNode}
            </span>
          ) : (
            valueNode
          )
        );
      }
      case "date":
        return wrapWithLink(<PrettyDate dateString={value} />);
      case "datetime":
        return wrapWithLink(<PrettyDate dateString={value} showTime={true} />);
      case "downloadurl":
      case "actionBtn":
      case "clickableColumn":
      case "clickabledate":
      case "specificAction":
      case "toggleValue":
        return renderClickableColumn(column, item, value);
      case "checkbox":
        return (
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
            checked={!!value}
            onChange={() => column.onClick?.(item)}
          />
        );
      default:
        // A card line with an empty text would be blank, and an empty link or button has no size to tap.
        // Only phones get the dash; the table prints an empty cell as it always did.
        const displayValue = isPhone && value === "" ? "-" : value;
        if (column.url) {
          return wrapWithLink(
            <span className="text-gray-900">{displayValue}</span>
          );
        } else if (column.onClick) {
          return (
            <button
              type="button"
              className="text-[#3F3227] transition-colors hover:text-[#B54708] hover:underline"
              onClick={() => column.onClick?.(item)}
            >
              {displayValue}
            </button>
          );
        }
        const suffixValue = column.suffix ? _.get(item, column.suffix) : "";
        const normalizedSuffix =
          suffixValue === null || suffixValue === undefined ? "" : suffixValue;
        return `${displayValue}${
          normalizedSuffix ? ` ${normalizedSuffix}` : ""
        }`;
    }
  };

  // How many cells actually joined each column's sum — a column whose rows are
  // all empty has no total to show, not a total of 0.
  const summedCounts: number[] = columns.map(() => 0);

  const tableData = collection.map((item: any, index: number) =>
    columns.map((column: Column, columnIndex: number) => {
      // Keep the raw value for the totals check below — it must not see the
      // "-" placeholder that a null/undefined cell renders as.
      const rawValue = _.get(item, column.key);

      if (
        column.isDisabledTotal !== true &&
        (column.type === "currency" || column.type === "number")
      ) {
        if (!column.totalType || column.totalType === "sum") {
          // A null/missing cell must not join the sum: today it fell through
          // as "-" and turned the running total into a string ("0-1"...).
          if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
            totals[columnIndex] += rawValue;
            summedCounts[columnIndex] += 1;
          }
        }
      }

      if (column.key === "#") {
        return index + 1;
      }

      return renderColumnValue(column, item);
    })
  );

  const totalsFormatted: any[] | undefined = !isTotalsDisabled
    ? columns.map((column: Column, index: number) => {
        if (column.isDisabledTotal === true) return "";
        if (
          (column.type === "currency" || column.type === "number") &&
          collection.length > 0 &&
          summedCounts[index] === 0
        ) {
          return "-";
        }
        if (column.formatTotal) return column.formatTotal(totals[index]);
        if (column.type === "currency") {
          return (
            <Currency value={totals[index]} isSymbol={true} isDecimal={true} />
          );
        }
        if (column.type === "number") {
          return <Currency value={totals[index]} />;
        }
        if (column.key === "#") {
          return collection.length;
        }
        return "";
      })
    : undefined;

  if (isPhone) {
    // The table's sums sit in its header; a card list has no header, so they go under the cards.
    const phoneTotals = columns
      .map((column, index) => ({ column, total: totalsFormatted?.[index] }))
      .filter(
        ({ column, total }) => column.name && total !== "" && total !== undefined
      );

    return (
      <Fragment>
        <div role="list">
          {collection.map((item: any, index: number) => (
            <DataTablePhoneCard
              key={index}
              item={item}
              columns={columns}
              renderColumnValue={renderColumnValue}
              highlightClassName={resolveRowClassName(item)}
              onOpen={isRowOpenable ? () => openRow(item) : undefined}
            />
          ))}
        </div>
        {collection.length > 0 && phoneTotals.length > 0 && (
          <div className={PHONE_SUMMARY_BOX_CLASS}>
            {phoneTotals.map(({ column, total }) => (
              <div
                key={column.key}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="text-gray-600">{column.name}</span>
                <span className="tabular-nums font-medium">{total}</span>
              </div>
            ))}
          </div>
        )}
        {footerRows && footerRows.length > 0 && (
          <div className={PHONE_SUMMARY_BOX_CLASS}>
            {footerRows.map((row) =>
              row.note ? (
                <div key={row.key} className={row.className || "text-xs text-gray-600"}>
                  {row.note}
                </div>
              ) : (
                <div key={row.key} className={`flex flex-wrap items-baseline justify-between gap-x-3 ${row.className || ""}`}>
                  <span className="text-gray-600">{row.label}</span>
                  <span className="flex flex-wrap gap-x-3 tabular-nums font-medium">
                    {Object.entries(row.cells || {}).map(([cellKey, value]) => (
                      <span key={cellKey}>{value}</span>
                    ))}
                  </span>
                </div>
              )
            )}
          </div>
        )}
      </Fragment>
    );
  }

  const hasTotalsRow =
    !!totalsFormatted &&
    totalsFormatted.some((value) => value !== "" && value !== undefined);

  const getCellAlignment = (column: Column) =>
    column.type === "currency" || column.type === "number"
      ? "text-right"
      : "text-left";

  return (
    <div
      className="bg-white rounded-sm border border-[#E6E1DB] overflow-hidden"
      style={wrapperStyle}
    >
      <div
        className="overflow-x-auto overflow-y-auto"
        data-scroll-restore="datatable"
        style={
          maxBodyHeight !== undefined ? { maxHeight: maxBodyHeight } : undefined
        }
      >
        <table className="w-full border-separate border-spacing-0">
          <thead className="sticky top-0 z-20">
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-sm text-gray-600 whitespace-normal align-top ${getCellAlignment(
                    column
                  )}`}
                  style={headerCellStyle}
                >
                  {column.header ?? column.name ?? ""}
                </th>
              ))}
            </tr>
            {hasTotalsRow && (
              <tr className="bg-gray-50 border-b border-gray-200" style={totalsRowStyle}>
                {totalsFormatted?.map((total, index) => (
                  <th
                    key={`total-${columns[index]?.key || index}`}
                    className={`px-4 py-3 text-sm font-normal bg-gray-50 ${getCellAlignment(
                      columns[index]
                    )}`}
                    style={bodyCellStyle}
                  >
                    {total}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => {
                const rowItem = collection[rowIndex];
                const rowKey = rowItem?.id ?? rowItem?.key ?? rowIndex;
                const isRowClickable = isRowOpenable && !!rowItem;
                const extraRowClass = rowItem ? resolveRowClassName(rowItem) : "";
                const stripeClass = rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/30";
                return (
                  <tr
                    key={rowKey}
                    className={`border-b border-gray-100 last:border-b-0 transition-colors ${
                      extraRowClass || `${stripeClass} hover:bg-gray-50`
                    } ${isRowClickable ? "cursor-pointer" : ""}`}
                    style={extraRowClass ? undefined : getBodyRowStyle(rowIndex)}
                    onClick={
                      isRowClickable ? () => openRow(rowItem) : undefined
                    }
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={`${
                          columns[cellIndex]?.key || cellIndex
                        }-${rowKey}`}
                        className={`px-4 py-3 text-sm text-gray-900 ${getCellAlignment(
                          columns[cellIndex]
                        )}`}
                        style={bodyCellStyle}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-sm text-gray-500"
                  style={bodyCellStyle}
                >
                  {t("no_data") || "Nėra duomenų"}
                </td>
              </tr>
            )}
          </tbody>
          {footerRows && footerRows.length > 0 && (
            <tfoot>
              {footerRows.map((row) => {
                if (row.note) {
                  return (
                    <tr key={row.key} className={row.className}>
                      <td colSpan={columns.length} className="px-4 py-2 text-xs" style={bodyCellStyle}>
                        {row.note}
                      </td>
                    </tr>
                  );
                }
                const cells = row.cells || {};
                let firstCellIndex = columns.findIndex((column) => column.key in cells);
                if (firstCellIndex === -1) firstCellIndex = columns.length;
                return (
                  <tr key={row.key} className={row.className}>
                    {firstCellIndex > 0 && (
                      <td
                        colSpan={firstCellIndex}
                        className="px-4 py-2 text-sm font-medium text-gray-700"
                        style={bodyCellStyle}
                      >
                        {row.label}
                      </td>
                    )}
                    {columns.slice(firstCellIndex).map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-2 text-sm tabular-nums ${getCellAlignment(column)}`}
                        style={bodyCellStyle}
                      >
                        {cells[column.key] ?? ""}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default DataTable;
